import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import DataTablePagination from './DataTablePagination';
import { formatInventoryLedgerSourceForDisplay } from '@/shared/inventoryLedgerSourceDisplay';
import { featureFlags } from '@/utils/featureFlags';
interface InventoryDataTableProps {
  data: any[];
  isLoading: boolean;
  totalCount?: number;
  currentPage?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (column: string | null, direction: 'asc' | 'desc') => void;
}

const InventoryDataTable: React.FC<InventoryDataTableProps> = ({
  data,
  isLoading,
  totalCount = 0,
  currentPage = 1,
  rowsPerPage = 50,
  onPageChange,
  onRowsPerPageChange,
  sortColumn: externalSortColumn,
  sortDirection: externalSortDirection = 'desc',
  onSortChange
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Use external sort state if provided, otherwise use local state
  const sortColumn = externalSortColumn;
  const sortDirection = externalSortDirection;

  // Reset expanded rows when data changes
  useEffect(() => {
    setExpandedRows(new Set());
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          Loading inventory data...
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No inventory data available for the selected filters.
        </CardContent>
      </Card>
    );
  }

  // Get column headers from the first row, excluding sourceBreakdown and constituent_skus
  // Rename SKU-like columns to 'Product' for display
  const rawColumns = Object.keys(data[0]).filter(
    col =>
      col !== 'sourceBreakdown' &&
      col !== 'sourceBreakdownRaw' &&
      col !== 'Snapshot Captured At' &&
      col !== 'constituent_skus' &&
      col !== 'identifier_missing' &&
      col !== '_identifier_type' &&
      !col.startsWith('_')
  );
  const isSkuColumn = (col: string) => col === 'SKU' || col === 'SKU (Unique Identifier)';
  const columns = rawColumns.map(col => isSkuColumn(col) ? 'Product' : col);

  // Remap data rows so SKU key becomes 'Product'
  const skuCol = rawColumns.find(isSkuColumn);
  const remappedData = skuCol
    ? data.map(row => {
      const { [skuCol]: skuVal, ...rest } = row;
      return { Product: skuVal, ...rest };
    })
    : data;

  // Find quantity column name
  const quantityColumn = columns.find(col =>
    ['Held', 'held', 'Quantity', 'quantity', 'stock_level', 'available_quantity'].includes(col)
  );

  const handleSort = (column: string) => {
    if (column === quantityColumn && onSortChange) {
      if (sortColumn === column) {
        const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange(column, newDirection);
      } else {
        onSortChange(column, 'desc');
      }
      // Reset to first page when sorting changes
      if (onPageChange) {
        onPageChange(1);
      }
    }
  };

  // Data is already sorted by backend, no need to sort on frontend
  const displayData = remappedData;

  // Calculate total pages from backend totalCount
  const totalPages = Math.ceil((totalCount || data.length) / rowsPerPage);

  const toggleRowExpansion = (index: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  // Parse constituent_skus safely from array or JSON string
  const getConstituents = (row: any): Array<{ asin: string; seller_sku: string }> => {
    let cs = row?.constituent_skus;
    if (typeof cs === 'string') {
      try { cs = JSON.parse(cs); } catch { return []; }
    }
    if (!Array.isArray(cs)) return [];
    return cs.filter((c: any) => c && (c.asin || c.seller_sku));
  };

  const hasConstituents = (row: any): boolean => getConstituents(row).length >= 1;

  // For identifier_missing rows with no constituent_skus, fall back to row.Product
  const getDisplayConstituents = (row: any): Array<{ asin: string; seller_sku: string }> => {
    const cs = getConstituents(row);
    if (cs.length > 0) return cs;
    if (row.identifier_missing === true) {
      const fallback = row.Product;
      if (fallback) return [{ asin: '', seller_sku: String(fallback) }];
    }
    return [];
  };

  const hasMultipleSources = (row: any): boolean => {
    // True if breakdown lists more than one source (even if some are zero)
    if (row.sourceBreakdown && typeof row.sourceBreakdown === 'object') {
      const keys = Object.keys(row.sourceBreakdown);
      if (keys.length > 1) return true;
    }
    // Fallback: comma-separated locations imply multiple sources
    const location = row.Location || row.location || row.Source || row.source || row.marketplace || '';
    return String(location).includes(',');
  };

  const formatCellValue = (value: any, isExpanded: boolean = false, columnName?: string) => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    let stringValue = String(value);

    // Format source column values
    if (columnName?.toLowerCase() === 'source') {
      stringValue = formatInventoryLedgerSourceForDisplay(stringValue);
    }

    if (!isExpanded && stringValue.length > 50) {
      return stringValue.substring(0, 50) + '...';
    }

    return stringValue;
  };

  const isLongContent = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    return String(value).length > 50;
  };

  const CellContent: React.FC<{ value: any; isExpanded: boolean; columnName?: string }> = ({ value, isExpanded, columnName }) => {
    const displayValue = formatCellValue(value, isExpanded, columnName);
    const fullValue = String(value || '-');
    const hasLongContent = isLongContent(value);

    if (!hasLongContent || isExpanded) {
      return <span>{displayValue}</span>;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help">{displayValue}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="whitespace-pre-wrap">{fullValue}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
      </CardHeader>
      <CardContent>
        <div className="overflow-auto max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                {columns.map((column) => {
                  const isSortable = column === quantityColumn;
                  const isSorted = sortColumn === column;

                  return (
                    <TableHead
                      key={column}
                      className={cn(
                        "font-semibold",
                        isSortable && "cursor-pointer hover:bg-muted/50"
                      )}
                      onClick={() => isSortable && handleSort(column)}
                    >
                      <div className="flex items-center gap-1">
                        {column}
                        {isSortable && (
                          <span className="text-xs text-muted-foreground">
                            {isSorted ? (sortDirection === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayData.map((row, index) => {
                const isExpanded = expandedRows.has(index);
                const hasExpandableContent = columns.some(col => isLongContent(row[col])) || hasMultipleSources(row) || hasConstituents(row) || row.identifier_missing === true;

                return (
                  <React.Fragment key={index}>
                    <TableRow
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        isExpanded && "bg-muted/30"
                      )}
                      onClick={() => hasExpandableContent && toggleRowExpansion(index)}
                    >
                      <TableCell className="w-8">
                        {hasExpandableContent && (
                          <div className="flex items-center justify-center">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                        )}
                      </TableCell>
                      {columns.map((column) => {
                        const isQuantityCell = column === quantityColumn;
                        const hasAdjustment = isQuantityCell && (
                          (typeof row._adjustmentDelta === 'number' && row._adjustmentDelta !== 0) ||
                          (typeof row._salesDelta === 'number' && row._salesDelta !== 0)
                        );
                        return (
                          <TableCell key={column} className="max-w-xs">
                            <span className="inline-flex items-center gap-1">
                              {column === 'Product' && row.identifier_missing === true ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p>No matching product identifier found. Expand to see seller SKU.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : (
                                <CellContent value={row[column]} isExpanded={isExpanded} columnName={column} />
                              )}
                              {featureFlags.showInventoryAdjust && hasAdjustment && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className="ml-1 inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary cursor-help"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        adjusted
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p>
                                        Snapshot {Number(row._snapshotQty ?? 0).toLocaleString()}
                                        {typeof row._adjustmentDelta === 'number' && row._adjustmentDelta !== 0 && (
                                          <>{' · '}Adjustments {row._adjustmentDelta > 0 ? '+' : ''}{Number(row._adjustmentDelta).toLocaleString()}</>
                                        )}
                                        {typeof row._salesDelta === 'number' && row._salesDelta !== 0 && (
                                          <>{' · '}Sales {row._salesDelta > 0 ? '+' : ''}{Number(row._salesDelta).toLocaleString()}</>
                                        )}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Source breakdown expansion */}
                    {isExpanded && hasMultipleSources(row) && (
                      <TableRow className="bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={columns.length} className="py-3">
                          <div className="pl-4">
                            <div className="text-sm font-semibold mb-2 text-muted-foreground">Stock Breakdown by Source:</div>
                            <div className="grid grid-cols-2 gap-2 max-w-md">
                              {Object.entries(row.sourceBreakdown || {})
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([source, quantity]) => {
                                  const numQuantity = typeof quantity === 'number' ? quantity : parseFloat(String(quantity || '0'));
                                  return (
                                    <div key={source} className="flex justify-between items-center p-2 bg-background rounded border">
                                      <span className="font-medium">{source}:</span>
                                      <span className="text-muted-foreground">{numQuantity.toLocaleString()} units</span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Identifier missing: show seller SKU when no constituent_skus available */}
                    {isExpanded && row.identifier_missing === true && !hasConstituents(row) && getDisplayConstituents(row).length > 0 && (
                      <TableRow className="bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={columns.length} className="py-3">
                          <div className="pl-4">
                            <div className="text-sm font-semibold mb-2 text-muted-foreground">Source Identifier:</div>
                            <div className="grid grid-cols-2 gap-2 max-w-md">
                              {getDisplayConstituents(row).map((c, ci) => (
                                <div key={ci} className="flex flex-col p-2 bg-background rounded border">
                                  {c.asin && <span className="text-xs text-muted-foreground">ASIN: <span className="font-medium text-foreground">{c.asin}</span></span>}
                                  {c.seller_sku && <span className="text-xs text-muted-foreground">Seller SKU: <span className="font-medium text-foreground">{c.seller_sku}</span></span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Constituent SKUs expansion */}
                    {isExpanded && hasConstituents(row) && (
                      <TableRow className="bg-muted/20">
                        <TableCell />
                        <TableCell colSpan={columns.length} className="py-3">
                          <div className="pl-4">
                            <div className="text-sm font-semibold mb-2 text-muted-foreground">Mapped Products:</div>
                            <div className="grid grid-cols-2 gap-2 max-w-md">
                              {getConstituents(row).map((c, ci) => (
                                <div key={ci} className="flex flex-col p-2 bg-background rounded border">
                                  {c.asin && <span className="text-xs text-muted-foreground">ASIN: <span className="font-medium text-foreground">{c.asin}</span></span>}
                                  {c.seller_sku && <span className="text-xs text-muted-foreground">Seller SKU: <span className="font-medium text-foreground">{c.seller_sku}</span></span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {onPageChange && totalPages > 1 && (
          <div className="mt-4">
            <div className="text-sm text-center text-muted-foreground mb-2">
              Showing page {currentPage} of {totalPages} ({displayData.length} of {totalCount} entries)
            </div>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InventoryDataTable;